import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Subscriptions = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchCurrentSubscription();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch('/api/subscriptions/plans', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.filter(plan => plan.is_active));
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await fetch('/api/subscriptions/current', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error fetching current subscription:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    try {
      const response = await fetch('/api/subscriptions/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ planId })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create checkout session');
        setSubscribing(null);
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout process');
      setSubscribing(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await fetch('/api/subscriptions/billing-portal', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        toast.error('Failed to open billing portal');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Failed to open billing portal');
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Subscription Plans</h1>
        <p className="mt-2 text-gray-600">
          Choose the plan that's right for you. All plans include full access to FieldHouse features.
        </p>
      </div>

      {/* Current Subscription Status */}
      {currentSubscription && (
        <div className="mb-8 bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Current Subscription</h2>
              <div className="mt-2 flex items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(currentSubscription.status)}`}>
                  {currentSubscription.status.charAt(0).toUpperCase() + currentSubscription.status.slice(1).replace('_', ' ')}
                </span>
                <span className="text-gray-600">
                  {currentSubscription.Plan?.name} - {formatPrice(currentSubscription.Plan?.amount)}/{currentSubscription.Plan?.interval}
                </span>
              </div>
              {currentSubscription.current_period_end && (
                <p className="mt-2 text-sm text-gray-500">
                  {currentSubscription.status === 'active' || currentSubscription.status === 'trialing' 
                    ? `Renews on ${formatDate(currentSubscription.current_period_end)}`
                    : `Expires on ${formatDate(currentSubscription.current_period_end)}`}
                </p>
              )}
            </div>
            <button
              onClick={handleManageBilling}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Manage Billing
            </button>
          </div>
        </div>
      )}

      {/* Subscription Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = currentSubscription?.Plan?.id === plan.id;
          const features = Array.isArray(plan.features) ? plan.features : [];
          
          return (
            <div
              key={plan.id}
              className={`bg-white rounded-lg shadow-lg border-2 overflow-hidden ${
                isCurrentPlan ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'
              }`}
            >
              {/* Plan Header */}
              <div className={`px-6 py-4 ${isCurrentPlan ? 'bg-primary-50' : 'bg-gray-50'}`}>
                <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-gray-900">
                    {formatPrice(plan.amount)}
                  </span>
                  <span className="text-gray-600">/{plan.interval}</span>
                </div>
                {plan.description && (
                  <p className="mt-2 text-sm text-gray-600">{plan.description}</p>
                )}
              </div>

              {/* Plan Features */}
              <div className="px-6 py-6">
                <ul className="space-y-3">
                  {features.length > 0 ? (
                    features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-500 text-sm">No features listed</li>
                  )}
                </ul>

                {/* Plan Limits */}
                {(plan.max_teams || plan.max_users_per_team) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      {plan.max_teams && `Up to ${plan.max_teams} teams`}
                      {plan.max_teams && plan.max_users_per_team && ' • '}
                      {plan.max_users_per_team && `Up to ${plan.max_users_per_team} users per team`}
                    </p>
                  </div>
                )}
              </div>

              {/* Plan Action Button */}
              <div className="px-6 py-4 bg-gray-50">
                {isCurrentPlan ? (
                  <button
                    disabled
                    className="w-full px-4 py-2 bg-gray-300 text-gray-600 rounded-md cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id}
                    className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {subscribing === plan.id ? 'Processing...' : 'Subscribe'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No subscription plans available at this time.</p>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
